import { SkeletonRows } from '../../components/LoadingSkeleton/LoadingSkeleton';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { QRCodeSVG } from 'qrcode.react';
import * as XLSX from 'xlsx';
import Swal from 'sweetalert2';
import {
  fetchOLEDDevices,
  createOLEDDevice,
  editOLEDDevice,
  deleteOLEDDevice,
  getOLEDSerialNumber
} from '../../features/clients/clientsSlice';
import { fetchOffices, fetchDevices } from '../../features/clients/clientsSlice';
import './oleddevices.css';
import TrashIcon from '../../components/icons/TrashIcon';

const OLEDDevices = () => {
  const dispatch = useDispatch();
  const { oledDevices, offices, loading } = useSelector((state) => state.clients);
  const user = useSelector((state) => state.auth?.user || JSON.parse(localStorage.getItem('admin_user') || '{}'));

  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Dialog states
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [isQrDialogOpen, setIsQrDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(-1);

  // Form state
  const [formData, setFormData] = useState({
    sno: '',
    devId: '',
    cbid: '',
    snostate: '',
    status: 'NotAssigned'
  });

  // QR state
  const [qrData, setQrData] = useState(null);

  const states = [
    "AL", "AK", "AS", "AZ", "AR", "CA", "CO", "CT", "DE", "DC", "FM", "FL", "GA", "GU", "HI",
    "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MH", "MD", "MA", "MI", "MN", "MS", "MO",
    "MT", "NE", "NV", "NH", "NJ", "NM", "NY", "NC", "ND", "MP", "OH", "OK", "OR", "PW", "PA",
    "PR", "RI", "SC", "SD", "TN", "TX", "UT", "VT", "VI", "VA", "WA", "WV", "WI", "WY",
    "INDIA", "INDIAV2", "INDIAV3","INDIAV4","CAV4","INDIAMT", "INDIASE", "BZ"
  ];

  useEffect(() => {
    dispatch(fetchOLEDDevices());
    dispatch(fetchOffices());
  }, [dispatch]);

  const filteredDevices = useMemo(() => {
    if (!oledDevices) return [];
    return oledDevices.filter(device =>
      [device.sno, device.devid, device.cbid, device.dealerName]
        .some(field => field && String(field).toLowerCase().includes(searchTerm.toLowerCase()))
    );
  }, [oledDevices, searchTerm]);

  // ============ PAGINATION - FIXED ============
  const totalItems = filteredDevices.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / rowsPerPage));

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Reset to page 1 when rows per page changes
  useEffect(() => {
    setCurrentPage(1);
  }, [rowsPerPage]);

  // Ensure current page is valid
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
    if (currentPage < 1) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  const indexOfFirst = (currentPage - 1) * rowsPerPage;
  const indexOfLast = Math.min(indexOfFirst + rowsPerPage, totalItems);
  const currentItems = filteredDevices.slice(indexOfFirst, indexOfLast);

  // Calculate displayed range
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const endItem = Math.min(currentPage * rowsPerPage, totalItems);

  // Pagination handlers
  const handlePageChange = useCallback((page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      // Scroll to top of table
      const tableElement = document.querySelector('.oled-table-container');
      if (tableElement) {
        tableElement.scrollTop = 0;
      }
    }
  }, [totalPages]);

  const handleRowsPerPageChange = useCallback((e) => {
    const newRowsPerPage = Number(e.target.value);
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1);
  }, []);

  // Generate page numbers for display - FIXED
  const getPageNumbers = useCallback(() => {
    const pages = [];
    const maxVisible = 5;
    const halfVisible = Math.floor(maxVisible / 2);

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      let startPage = Math.max(1, currentPage - halfVisible);
      let endPage = Math.min(totalPages, currentPage + halfVisible);

      // Adjust if we're at the beginning
      if (currentPage - halfVisible < 1) {
        endPage = Math.min(totalPages, endPage + (halfVisible - currentPage + 1));
      }
      
      // Adjust if we're at the end
      if (currentPage + halfVisible > totalPages) {
        startPage = Math.max(1, startPage - (currentPage + halfVisible - totalPages));
      }

      // Add first page and ellipsis if needed
      if (startPage > 1) {
        pages.push(1);
        if (startPage > 2) {
          pages.push('...');
        }
      }

      // Add middle pages
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      // Add last page and ellipsis if needed
      if (endPage < totalPages) {
        if (endPage < totalPages - 1) {
          pages.push('...');
        }
        pages.push(totalPages);
      }
    }

    return pages;
  }, [currentPage, totalPages]);

  const handleStateChange = async (state) => {
    setFormData(prev => ({ ...prev, snostate: state }));
    if (state && editingIndex === -1) {
      try {
        const result = await dispatch(getOLEDSerialNumber(state)).unwrap();
        const mynum = parseInt(result) + 1;
        const sno = "612" + state + String(mynum).padStart(5, '0');
        setFormData(prev => ({ ...prev, sno: sno }));
      } catch (err) {
        Swal.fire('Error', 'Failed to generate serial number', 'error');
      }
    }
  };

  const handleEdit = (device) => {
    setEditingIndex(oledDevices.indexOf(device));
    let state = '';
    if (device.sno) {
      if (device.sno.length === 10) {
        state = device.sno.substring(3, 5);
      } else {
        state = device.sno.substring(3, 8);
      }
    }
    setFormData({
      sno: device.sno || '',
      devId: device.devid || '',
      cbid: device.cbid || '',
      snostate: state,
      status: device.status || 'NotAssigned'
    });
    setIsDialogOpen(true);
  };

  const handleDelete = (device) => {
    Swal.fire({
      title: `Are you sure you want to delete ${device.devid}?`,
      text: "You can't revert your action!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'No, keep it!'
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(deleteOLEDDevice(device.id)).then(() => {
          Swal.fire('Deleted!', 'Device has been deleted.', 'success');
        });
      }
    });
  };

  const handleSave = async () => {
    if (!formData.sno || !formData.devId || !formData.cbid) {
      Swal.fire('Error', 'Please fill all required fields', 'error');
      return;
    }

    const fd = new FormData();
    fd.append("sno", formData.sno);
    fd.append("devid", formData.devId);
    fd.append("cbid", formData.cbid);

    try {
      if (editingIndex === -1) {
        await dispatch(createOLEDDevice(fd)).unwrap();
        Swal.fire('Success', 'Device added successfully', 'success');
      } else {
        await dispatch(editOLEDDevice({ id: formData.sno, devdata: fd })).unwrap();
        Swal.fire('Success', 'Device updated successfully', 'success');
      }
      setIsDialogOpen(false);
      resetForm();
    } catch (err) {
      Swal.fire('Error', err || 'Operation failed', 'error');
    }
  };

  const resetForm = () => {
    setFormData({
      sno: '',
      devId: '',
      cbid: '',
      snostate: '',
      status: 'NotAssigned'
    });
    setEditingIndex(-1);
  };

  const handleExport = (type) => {
    const worksheetData = [
      ["id", "sno", "devid", "cbid"],
      ...oledDevices.map(row => [row.id, row.sno, row.devid, row.cbid])
    ];
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "oleddevices");

    if (type === 'csv') {
      XLSX.writeFile(workbook, "oled_devices.xlsx");
    } else {
      XLSX.writeFile(workbook, "oled_devices.txt", { bookType: 'txt' });
    }
    setIsExportDialogOpen(false);
  };

  const showQR = (device) => {
    const alldate = {
      sno: device.sno,
      devId: device.devid,
      cbid: device.cbid,
      calibrationdate: device.calibrationdate,
      status: device.status,
      dealerName: device.dealerName
    };
    setQrData(JSON.stringify(alldate));
    setIsQrDialogOpen(true);
  };

  const isAdmin = user?.role === "Admin" || user?.role === "superadmin";

  return (
    <div className="oled-app">
      <div className="oled-card">
        {/* Header */}
        <div className="oled-header">
          <div>
            <h1 className="oled-title">OLED Devices</h1>
            <p className="oled-subtitle">Manage OLED display devices and configurations</p>
          </div>
          <div className="header-actions">
            <div className="stat-pill">
              <span className="stat-dot" />
              <span className="stat-label">Records</span>
              <span className="stat-value">{filteredDevices.length}</span>
            </div>
            {isAdmin && (
              <button className="export-btn" onClick={() => setIsExportDialogOpen(true)}>
                Export
              </button>
            )}
            <button className="add-oled-btn" onClick={() => { resetForm(); setIsDialogOpen(true); }}>
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M9 3v12M3 9h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              Add OLED Device
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="oled-filters">
          <div className="search-wrapper">
            <svg className="search-svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
              <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" />
              <path d="M12.5 12.5L16 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            <input
              type="text"
              className="search-input"
              placeholder="Search by Device ID, HandSet, or SNO..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            />
          </div>
          <div className="records-info">
            <span className="records-label">Show</span>
            <select
              className="records-select"
              value={rowsPerPage}
              onChange={handleRowsPerPageChange}
            >
              {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            <span className="records-label">entries</span>
          </div>
        </div>

        {/* Table */}
        <div className="oled-table-container">
          <table className="oled-table">
            <thead>
              <tr>
                <th>SNO</th>
                {isAdmin ? null : <th>Dealer</th>}
                <th>Device ID (CBID)</th>
                <th>HandSet (DevID)</th>
                {isAdmin ? null : <th>Status</th>}
                <th className="text-center">Actions</th>
                {isAdmin ? <th className="text-center">Delete</th> : <th className="text-center">QR</th>}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <SkeletonRows columns={isAdmin ? 5 : 7} />
              ) : currentItems.length > 0 ? (
                currentItems.map((device, idx) => (
                  <tr key={device.id || idx}>
                    <td className="sno-cell">{device.sno}</td>
                    {isAdmin ? null : <td className="dealer-cell">{device.dealerName}</td>}
                    <td className="cbid-cell">{device.cbid}</td>
                    <td className="devid-cell">{device.devid}</td>
                    {isAdmin ? null : <td className="status-cell">{device.status}</td>}
                    <td className="text-center">
                      <button className="action-icon edit" title="Edit" onClick={() => handleEdit(device)}>
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                          <path d="M10 1L13 4L4.5 12.5H1V9L10 1Z" stroke="currentColor" strokeWidth="1.2" />
                        </svg>
                      </button>
                    </td>
                    <td className="text-center">
                      {isAdmin ? (
                        <button className="action-icon delete" title="Delete" onClick={() => handleDelete(device)}>
                          <TrashIcon />
                        </button>
                      ) : (
                        <button className="action-icon qr" title="QR Code" onClick={() => showQR(device)}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                            <path d="M7 7h4v4H7zM13 7h4v4h-4zM7 13h4v4H7z"></path>
                            <path d="M13 13h1v1h-1zM16 13h1v1h-1zM15 14h1v1h-1zM13 16h1v1h-1zM15 16h1v1h-1zM17 16h1v1h-1zM14 15h1v1h-1zM16 15h1v1h-1z"></path>
                          </svg>
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan="7" className="text-center py-8">No data found</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination - FIXED */}
        <div className="pagination-container">
          <div className="pagination-info">
            Showing <strong>{startItem}–{endItem}</strong> of <strong>{totalItems}</strong> entries
          </div>
          <div className="pagination-controls">
            <button 
              className="page-btn" 
              onClick={() => handlePageChange(1)} 
              disabled={currentPage === 1 || totalPages === 1}
            >
              «
            </button>
            <button 
              className="page-btn" 
              onClick={() => handlePageChange(currentPage - 1)} 
              disabled={currentPage === 1 || totalPages === 1}
            >
              ‹
            </button>
            {getPageNumbers().map((p, i) => (
              p === '...'
                ? <span key={`ellipsis-${i}`} className="page-dots">…</span>
                : (
                  <button 
                    key={`page-${p}`} 
                    className={`page-btn ${currentPage === p ? 'active' : ''}`} 
                    onClick={() => handlePageChange(p)}
                  >
                    {p}
                  </button>
                )
            ))}
            <button 
              className="page-btn" 
              onClick={() => handlePageChange(currentPage + 1)} 
              disabled={currentPage === totalPages || totalPages === 1}
            >
              ›
            </button>
            <button 
              className="page-btn" 
              onClick={() => handlePageChange(totalPages)} 
              disabled={currentPage === totalPages || totalPages === 1}
            >
              »
            </button>
          </div>
        </div>
      </div>

      {/* --- MODALS --- */}

      {/* Add/Edit Modal */}
      {isDialogOpen && (
        <div className="dialog-overlay" onClick={() => setIsDialogOpen(false)}>
          <div className="dialog-container" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header">
              <h2 className="dialog-title">{editingIndex === -1 ? 'Add Device' : 'Edit Device'}</h2>
              <button className="dialog-close" onClick={() => setIsDialogOpen(false)}>×</button>
            </div>
            <div className="dialog-body">
              <div className="form-group">
                <label>Select State*</label>
                <select
                  className="form-select"
                  value={formData.snostate}
                  onChange={(e) => handleStateChange(e.target.value)}
                  disabled={editingIndex !== -1}
                >
                  <option value="">-- Choose State --</option>
                  {states.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>SNO*</label>
                <input type="text" value={formData.sno} readOnly className="form-input readonly" />
              </div>
              <div className="form-group">
                <label>Device ID (HandSet)*</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.devId}
                  onChange={(e) => setFormData({ ...formData, devId: e.target.value })}
                  disabled={editingIndex !== -1}
                  placeholder="Enter HandSet ID"
                />
              </div>
              <div className="form-group">
                <label>Control Board ID*</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.cbid}
                  onChange={(e) => setFormData({ ...formData, cbid: e.target.value })}
                  disabled={editingIndex !== -1}
                  placeholder="Enter Control Board ID"
                />
              </div>
            </div>
            <div className="dialog-footer">
              <button className="dialog-btn cancel-btn" onClick={() => setIsDialogOpen(false)}>Cancel</button>
              <button className="dialog-btn save-btn" onClick={handleSave}>
                {editingIndex === -1 ? 'SAVE' : 'UPDATE'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      {isExportDialogOpen && (
        <div className="dialog-overlay" onClick={() => setIsExportDialogOpen(false)}>
          <div className="dialog-container small" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header">
              <h2 className="dialog-title">Please Choose The Format</h2>
              <button className="dialog-close" onClick={() => setIsExportDialogOpen(false)}>×</button>
            </div>
            <div className="dialog-body text-center">
              <button className="export-choice-btn excel" onClick={() => handleExport('csv')}>
                Excel / CSV
              </button>
            </div>
          </div>
        </div>
      )}

      {/* QR Code Modal */}
      {isQrDialogOpen && (
        <div className="dialog-overlay" onClick={() => setIsQrDialogOpen(false)}>
          <div className="dialog-container small" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header">
              <h2 className="dialog-title">QR Code</h2>
              <button className="dialog-close" onClick={() => setIsQrDialogOpen(false)}>×</button>
            </div>
            <div className="dialog-body text-center qr-body">
              {qrData && <QRCodeSVG value={qrData} size={200} />}
              <p className="qr-hint">Scan to view device details</p>
            </div>
            <div className="dialog-footer">
              <button className="dialog-btn print-btn" onClick={() => window.print()}>Print</button>
              <button className="dialog-btn cancel-btn" onClick={() => setIsQrDialogOpen(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OLEDDevices;
